import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Emotion.css";

interface EmotionItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Emotion: React.FC = () => {
  const [data, setData] = useState<EmotionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/emotion`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching emotions:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: EmotionItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: EmotionItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Emotions",
    de: "Emotionen",
    ta: "உணர்ச்சிகள்",
    si: "හැඟීම්",
    hi: "भावनाएँ",
    ar: "المشاعر",
    tr: "Duygular",
    ru: "Эмоции",
    zh: "情感",
    fr: "Émotions",
    es: "Emociones",
  };

  const subtitleMap = {
    en: "Emotions in German",
    de: "Emotionen auf Deutsch",
    ta: "ஜெர்மன் மொழியில் உணர்ச்சிகள்",
    si: "ජර්මන් භාෂාවෙන් හැඟීම්",
    hi: "जर्मन में भावनाएँ",
    ar: "المشاعر بالألمانية",
    tr: "Almanca'da Duygular",
    ru: "Эмоции на немецком",
    zh: "德语情感",
    fr: "Émotions en allemand",
    es: "Emociones en alemán",
  };

  return (
    <div className="emotion-page-container">
      <div className="emotion-page-header">
        <div>
          <h1 className="emotion-page-title">{titleMap["de"]}</h1>
          <p className="emotion-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="emotion-page-loading">Loading...</div>
      ) : (
        <div className="emotion-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;

            return (
              <div
                key={item.id}
                className={`emotion-page-card ${
                  isPlaying ? "emotion-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="emotion-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="emotion-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="emotion-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="emotion-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="emotion-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Emotion;
